import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const HelpModeContext = createContext(null);

export const useHelpMode = () => {
  const ctx = useContext(HelpModeContext);
  if (!ctx) throw new Error('useHelpMode must be used within HelpModeProvider');
  return ctx;
};

/**
 * HelpModeProvider (Feb 2026 · rev. onboarding one-shot)
 *
 *  Reglas de negocio:
 *   - En el PRIMER login de un usuario (cuando `help_mode_intro_seen` no está
 *     marcado en su perfil), el modo ayuda se activa automáticamente Y se
 *     muestra un toast informativo. En ese mismo momento persistimos
 *     `help_mode_enabled=true` y `help_mode_intro_seen=true` en el backend.
 *   - A partir de ese instante, el estado se lee SIEMPRE desde
 *     `user.help_mode_enabled`. Ninguna lógica del cliente vuelve a
 *     "auto-activarlo" — si el usuario lo desactiva, se queda desactivado
 *     entre sesiones; si lo reactiva manualmente, también se recuerda.
 *   - El toast informativo solo aparece la primera vez en toda la plataforma
 *     (gated por el flag persistido), nunca en logins posteriores.
 */
export const HelpModeProvider = ({ children }) => {
  const { user, checkAuth } = useAuth();
  const [enabled, setEnabledState] = useState(false);
  // Evita que `initOnFirstLogin` se dispare dos veces por render en StrictMode
  // (idempotente en cualquier caso porque marca `help_mode_intro_seen`).
  const initInFlight = useRef(false);

  // Sync con `user.help_mode_enabled` cada vez que cambia el usuario.
  // Sin defaults implícitos: si el flag está en false, respetamos false.
  useEffect(() => {
    if (!user) { setEnabledState(false); return; }
    setEnabledState(Boolean(user.help_mode_enabled));
  }, [user]);

  // Primer login del usuario en la plataforma: activar + persistir + toast.
  useEffect(() => {
    if (!user) return;
    if (user.help_mode_intro_seen) return; // ya lo vio antes
    if (initInFlight.current) return;
    initInFlight.current = true;
    (async () => {
      try {
        await axios.put(
          `${API}/auth/me`,
          { help_mode_enabled: true, help_mode_intro_seen: true },
          { withCredentials: true },
        );
        setEnabledState(true);
        if (typeof checkAuth === 'function') checkAuth();
        toast.info(
          'El modo ayuda está activado. Verás iconos de ayuda junto a cada campo. ' +
          'Puedes desactivarlo en la barra superior, junto al icono de notificaciones, ' +
          'y volver a activarlo cuando lo necesites.',
          { duration: 8000, id: 'help-mode-info' },
        );
      } catch (e) {
        // Si falla la persistencia, no marcamos como visto para reintentarlo
        // en el próximo login del usuario.
        initInFlight.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.help_mode_intro_seen]);

  const setEnabled = useCallback(async (next) => {
    const prev = enabled;
    setEnabledState(next);
    try {
      await axios.put(`${API}/auth/me`, { help_mode_enabled: next }, { withCredentials: true });
      if (typeof checkAuth === 'function') checkAuth();
    } catch (e) {
      setEnabledState(prev); // rollback local si falla el guardado
      toast.error('No se pudo guardar la preferencia');
    }
  }, [enabled, checkAuth]);

  return (
    <HelpModeContext.Provider value={{ enabled, setEnabled }}>
      {children}
    </HelpModeContext.Provider>
  );
};

export default HelpModeContext;
